import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/alert-dialog/alert-dialog';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { deleteQuestionBankMutationOptions } from '@/features/question-banks/api/delete-question-bank.api';
import { GET_LIST_QUESTION_BANK_QUERY_KEY } from '@/features/question-banks/api/get-list-question-bank.api';

export interface DeleteQuestionBankDialogProps extends BaseDialogProps {
  questionBankId: string;
  questionBankName: string;
}

export default function DeleteQuestionBankDialog({
  dialog,
  questionBankId,
  questionBankName,
}: DeleteQuestionBankDialogProps) {
  const { closeDeleteQuestionBankDialog } = useV2DialogContext();
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    ...deleteQuestionBankMutationOptions(),
    onSuccess: () => {
      toast.success('Bank soal berhasil dihapus');
      queryClient.invalidateQueries({
        queryKey: [GET_LIST_QUESTION_BANK_QUERY_KEY],
      });
      closeDeleteQuestionBankDialog();
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal menghapus bank soal';
      toast.error(message);
    },
  });

  const isPending = deleteMutation.isPending;

  return (
    <AlertDialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState && !isPending) {
          closeDeleteQuestionBankDialog();
        }
      }}
    >
      <AlertDialogContent showCloseButton={false}>
        <AlertDialogHeader>
          <AlertDialogTitle>Hapus bank soal ini?</AlertDialogTitle>
          <AlertDialogDescription>
            Tindakan ini tidak dapat dibatalkan. Bank soal "{questionBankName}"
            akan dihapus secara permanen dari sistem.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Batal</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={isPending}
            onClick={() =>
              deleteMutation.mutate({ question_bank_id: questionBankId })
            }
          >
            {isPending ? 'Menghapus...' : 'Hapus'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
